(function (root, factory) {
  'use strict';

  if (typeof module === 'object' && module.exports) {
    module.exports = { createMediaPlaybackHost: factory };
    return;
  }

  root.SAMediaPlayback = factory();
}(typeof globalThis !== 'undefined' ? globalThis : this, function createMediaPlaybackHost(options) {
  'use strict';

  options = options || {};
  var browserRoot = typeof globalThis !== 'undefined' ? globalThis : this;
  var platform = options.platform || createBrowserPlatform();
  var registrations = new Map();
  var decoded = new Map();
  var waveformCache = new Map();
  var inFlight = new Map();
  var loadOrder = 0;
  var cacheClock = 0;
  var activeLoads = 0;
  var maxConcurrentLoads = positiveInteger(options.maxConcurrentLoads, 4);
  var cacheMaxEntries = positiveInteger(options.cacheMaxEntries, 24);
  var cacheMaxBytes = positiveInteger(options.cacheMaxBytes, 128 * 1024 * 1024);
  var audibleContext = null;
  var currentOwner = null;
  var frameParticipants = new Set();
  var frameRequestId = null;

  function positiveInteger(value, fallback) {
    return Number.isInteger(value) && value > 0 ? value : fallback;
  }

  function publicError(code, message, clipId) {
    var error = new Error(message);
    error.code = code;
    if (clipId != null) error.clipId = clipId;
    return error;
  }

  function report(error, scope) {
    try {
      platform.log({ scope: scope, error: error });
    } catch (_) {
      // Diagnostics must never become a control dependency.
    }
  }

  function stableFailure(error, fallbackCode, clipId) {
    return Object.freeze({
      clipId: clipId == null ? null : clipId,
      code: error && error.code ? error.code : fallbackCode,
      message: error && error.message ? error.message : String(error || fallbackCode)
    });
  }

  function canonicalUrl(url) {
    return platform.canonicalizeUrl ? platform.canonicalizeUrl(url) : url;
  }

  function safeCanonicalUrl(url) {
    try {
      return canonicalUrl(url);
    } catch (error) {
      return null;
    }
  }

  function invalidUrlError(clip) {
    return publicError('INVALID_URL', 'Invalid clip URL.', clip && clip.id);
  }

  function getAudibleContext() {
    if (!audibleContext) audibleContext = platform.createAudibleContext();
    if (!audibleContext) throw publicError('UNSUPPORTED_AUDIO', 'Web Audio is unavailable.');
    return audibleContext;
  }

  function isolatePause(owner) {
    if (!owner || typeof owner.pause !== 'function') return;
    try {
      var result = owner.pause();
      if (result && typeof result.then === 'function') {
        result.catch(function (error) { report(error, 'participant-pause'); });
      }
    } catch (error) {
      report(error, 'participant-pause');
    }
  }

  function claim(owner) {
    if (currentOwner === owner) return;
    var previous = currentOwner;
    currentOwner = owner;
    isolatePause(previous);
  }

  function release(owner) {
    if (currentOwner === owner) currentOwner = null;
  }

  function scheduleFrame() {
    if (frameRequestId != null || frameParticipants.size === 0) return;
    frameRequestId = platform.requestAnimationFrame(runFrame);
  }

  function runFrame(timestamp) {
    frameRequestId = null;
    Array.from(frameParticipants).forEach(function (participant) {
      try {
        participant.update(timestamp);
      } catch (error) {
        report(error, 'animation-frame');
      }
    });
    scheduleFrame();
  }

  function addFrameParticipant(participant) {
    frameParticipants.add(participant);
    scheduleFrame();
  }

  function removeFrameParticipant(participant) {
    frameParticipants.delete(participant);
    if (frameParticipants.size === 0 && frameRequestId != null) {
      platform.cancelAnimationFrame(frameRequestId);
      frameRequestId = null;
    }
  }

  function priorityRank(priority) {
    return priority === 'user' ? 0 : (priority === 'near' ? 1 : 2);
  }

  function decodedBytes(buffer) {
    var length = Number(buffer && buffer.length) || 0;
    var channels = Number(buffer && buffer.numberOfChannels) || 1;
    return length * channels * 4;
  }

  function evictDecoded() {
    function totals() {
      var bytes = 0;
      decoded.forEach(function (entry) { bytes += entry.bytes; });
      return { entries: decoded.size, bytes: bytes };
    }
    var total = totals();
    while (total.entries > cacheMaxEntries || total.bytes > cacheMaxBytes) {
      var candidateKey = null;
      var candidate = null;
      decoded.forEach(function (entry, key) {
        if (entry.pins > 0) return;
        if (!candidate || entry.lastUsed < candidate.lastUsed) {
          candidate = entry;
          candidateKey = key;
        }
      });
      if (candidateKey == null) return;
      decoded.delete(candidateKey);
      total = totals();
    }
  }

  function storeDecoded(key, buffer) {
    var existing = decoded.get(key);
    decoded.set(key, {
      buffer: buffer,
      bytes: decodedBytes(buffer),
      pins: existing ? existing.pins : 0,
      lastUsed: ++cacheClock
    });
    evictDecoded();
  }

  function cachedBuffer(key) {
    var entry = decoded.get(key);
    if (!entry) return null;
    entry.lastUsed = ++cacheClock;
    return entry.buffer;
  }

  function pinDecoded(key, buffer) {
    var entry = decoded.get(key);
    if (!entry) {
      entry = { buffer: buffer, bytes: decodedBytes(buffer), pins: 0, lastUsed: ++cacheClock };
      decoded.set(key, entry);
    }
    entry.pins += 1;
    entry.lastUsed = ++cacheClock;
    evictDecoded();
  }

  function unpinDecoded(key) {
    var entry = key == null ? null : decoded.get(key);
    if (entry) entry.pins = Math.max(0, entry.pins - 1);
    evictDecoded();
  }

  function pumpLoads() {
    while (activeLoads < maxConcurrentLoads) {
      var queued = Array.from(inFlight.values()).filter(function (job) { return job.state === 'queued'; });
      if (queued.length === 0) return;
      queued.sort(function (left, right) {
        return priorityRank(left.priority) - priorityRank(right.priority) || left.order - right.order;
      });
      startLoad(queued[0]);
    }
  }

  function startLoad(job) {
    job.state = 'active';
    activeLoads += 1;
    Promise.resolve()
      .then(function () { return platform.fetchArrayBuffer(job.key); })
      .catch(function (error) {
        throw publicError('FETCH_FAILED', error && error.message ? error.message : 'Audio fetch failed.', job.clip.id);
      })
      .then(function (bytes) { return platform.decodeAudioData(bytes, audibleContext); })
      .catch(function (error) {
        if (error && (error.code === 'FETCH_FAILED' || error.code === 'UNSUPPORTED_AUDIO')) throw error;
        throw publicError('DECODE_FAILED', error && error.message ? error.message : 'Audio decode failed.', job.clip.id);
      })
      .then(function (buffer) {
        storeDecoded(job.key, buffer);
        finishLoad(job, null, buffer);
      }, function (error) {
        finishLoad(job, error);
      });
  }

  function finishLoad(job, error, buffer) {
    activeLoads -= 1;
    inFlight.delete(job.key);
    if (error) job.reject(error);
    else job.resolve(buffer);
    pumpLoads();
  }

  function loadClip(clip, priority) {
    var key = safeCanonicalUrl(clip.url);
    if (key == null) return Promise.reject(invalidUrlError(clip));
    var cached = cachedBuffer(key);
    if (cached) return Promise.resolve(cached);
    var existing = inFlight.get(key);
    if (existing) {
      if (existing.state === 'queued' && priorityRank(priority) < priorityRank(existing.priority)) {
        existing.priority = priority;
        pumpLoads();
      }
      return existing.promise;
    }

    var resolveJob;
    var rejectJob;
    var promise = new Promise(function (resolve, reject) {
      resolveJob = resolve;
      rejectJob = reject;
    });
    var job = {
      key: key,
      clip: clip,
      priority: priority,
      order: loadOrder++,
      state: 'queued',
      promise: promise,
      resolve: resolveJob,
      reject: rejectJob
    };
    inFlight.set(key, job);
    pumpLoads();
    return promise;
  }

  function snapshot(desired, audible, status, error) {
    return Object.freeze({
      desired: Object.freeze({
        clipId: desired.clipId,
        playing: desired.playing,
        positionSeconds: desired.positionSeconds
      }),
      audible: Object.freeze({
        clipId: audible.clipId,
        playing: audible.playing,
        positionSeconds: audible.positionSeconds,
        durationSeconds: audible.durationSeconds
      }),
      status: status,
      error: error
    });
  }

  function validateSessionConfig(config) {
    if (!config || typeof config.id !== 'string' || !config.id.trim()) {
      throw publicError('INVALID_CONFIG', 'A session requires a non-empty id.');
    }
    if (!Array.isArray(config.clips) || config.clips.length === 0) {
      throw publicError('INVALID_CONFIG', 'A session requires at least one clip.');
    }
    if (registrations.has(config.id)) {
      throw publicError('INVALID_CONFIG', 'The media id is already registered: ' + config.id);
    }

    var clips = new Map();
    config.clips.forEach(function (clip) {
      if (!clip || typeof clip.id !== 'string' || !clip.id.trim() ||
          typeof clip.url !== 'string' || !clip.url.trim() ||
          typeof clip.timelineId !== 'string' || !clip.timelineId.trim() ||
          clips.has(clip.id)) {
        throw publicError('INVALID_CONFIG', 'Every clip requires unique id, url, and timelineId values.');
      }
      clips.set(clip.id, Object.freeze({
        id: clip.id,
        url: clip.url,
        timelineId: clip.timelineId
      }));
    });

    var initialId = config.initialClipId == null ? config.clips[0].id : config.initialClipId;
    if (!clips.has(initialId)) {
      throw publicError('INVALID_CONFIG', 'initialClipId must identify a configured clip.');
    }
    return { id: config.id, clips: clips, initialClipId: initialId };
  }

  function createSession(config) {
    var valid = validateSessionConfig(config);
    var destroyed = false;
    var listeners = new Set();
    var desired = { clipId: valid.initialClipId, playing: false, positionSeconds: 0 };
    var audible = { clipId: null, playing: false, positionSeconds: 0, durationSeconds: 0 };
    var currentState = snapshot(desired, audible, 'idle', null);
    var intentVersion = 0;
    var source = null;
    var analyser = null;
    var analyserConnected = false;
    var spectrumScratch = null;
    var spectrumResult = { binsWritten: 0, sampleRate: 0 };
    var analysisSampleRate = 0;
    var startedAt = 0;
    var sourceOffset = 0;
    var timelinePositions = new Map();
    var pinnedKey = null;
    var owner = {
      id: valid.id,
      pause: pauseForOwnership
    };
    var frameParticipant = { update: updateForFrame };

    function assertAlive() {
      if (destroyed) throw publicError('SESSION_DESTROYED', 'The media session has been destroyed.');
    }

    function publish(status, error) {
      currentState = snapshot(desired, audible, status, error || null);
      listeners.forEach(function (listener) {
        try {
          listener(currentState);
        } catch (listenerError) {
          report(listenerError, 'listener');
        }
      });
    }

    function safeCall(target, method) {
      if (!target || typeof target[method] !== 'function') return;
      try {
        target[method]();
      } catch (error) {
        report(error, 'source-' + method);
      }
    }

    function stopSource() {
      removeFrameParticipant(frameParticipant);
      if (source) {
        var oldSource = source;
        source = null;
        oldSource.onended = null;
        safeCall(oldSource, 'stop');
        safeCall(oldSource, 'disconnect');
      }
      if (pinnedKey != null) {
        unpinDecoded(pinnedKey);
        pinnedKey = null;
      }
    }

    function currentPosition() {
      if (!source || !audible.playing || !audibleContext) return audible.positionSeconds;
      var elapsed = Math.max(0, audibleContext.currentTime - startedAt);
      var position = sourceOffset + elapsed;
      return audible.durationSeconds > 0 ? position % audible.durationSeconds : position;
    }

    function pauseForOwnership() {
      if (destroyed || !desired.playing) return;
      intentVersion += 1;
      var position = currentPosition();
      desired = { clipId: desired.clipId, playing: false, positionSeconds: position };
      audible = {
        clipId: audible.clipId,
        playing: false,
        positionSeconds: position,
        durationSeconds: audible.durationSeconds
      };
      stopSource();
      publish(audible.clipId == null ? 'idle' : 'paused', null);
    }

    function beginPlayback(version, clip, buffer) {
      if (destroyed || version !== intentVersion || !desired.playing || desired.clipId !== clip.id) {
        return currentState;
      }
      var context = getAudibleContext();
      stopSource();
      if (!analyser) {
        analyser = context.createAnalyser();
        spectrumScratch = new Uint8Array(Math.max(0, Number(analyser.frequencyBinCount) || 0));
      }
      if (!analyserConnected) {
        analyser.connect(context.destination);
        analyserConnected = true;
      }
      var duration = Number(buffer.duration) || 0;
      analysisSampleRate = Number(buffer.sampleRate) || Number(context.sampleRate) || 0;
      var offset = duration > 0 ? desired.positionSeconds % duration : desired.positionSeconds;
      var nextSource = context.createBufferSource();
      nextSource.buffer = buffer;
      nextSource.loop = true;
      nextSource.connect(analyser);
      nextSource.onended = function () {
        if (source !== nextSource || destroyed || !desired.playing) return;
        var endedPosition = currentPosition();
        intentVersion += 1;
        source = null;
        removeFrameParticipant(frameParticipant);
        safeCall(nextSource, 'disconnect');
        if (pinnedKey != null) {
          unpinDecoded(pinnedKey);
          pinnedKey = null;
        }
        release(owner);
        desired = {
          clipId: clip.id,
          playing: false,
          positionSeconds: endedPosition
        };
        audible = {
          clipId: clip.id,
          playing: false,
          positionSeconds: endedPosition,
          durationSeconds: duration
        };
        publish('paused', null);
      };
      source = nextSource;
      pinnedKey = canonicalUrl(clip.url);
      pinDecoded(pinnedKey, buffer);
      sourceOffset = offset;
      startedAt = context.currentTime;
      nextSource.start(0, offset);
      audible = {
        clipId: clip.id,
        playing: true,
        positionSeconds: offset,
        durationSeconds: duration
      };
      publish('playing', null);
      addFrameParticipant(frameParticipant);
      return currentState;
    }

    function updateForFrame() {
      if (destroyed || !audible.playing || !source) return;
      audible = {
        clipId: audible.clipId,
        playing: true,
        positionSeconds: currentPosition(),
        durationSeconds: audible.durationSeconds
      };
      publish('playing', null);
    }

    function set(patch) {
      assertAlive();
      patch = patch || {};
      if (patch.positionSeconds != null &&
          (typeof patch.positionSeconds !== 'number' || !Number.isFinite(patch.positionSeconds) || patch.positionSeconds < 0)) {
        throw publicError('INVALID_POSITION', 'positionSeconds must be a finite non-negative number.');
      }
      if (patch.clipId != null && !valid.clips.has(patch.clipId)) {
        throw publicError('UNKNOWN_CLIP', 'Unknown clip: ' + patch.clipId, patch.clipId);
      }
      if (patch.playing != null && typeof patch.playing !== 'boolean') {
        throw publicError('INVALID_CONFIG', 'playing must be a boolean.');
      }
      var oldClip = valid.clips.get(desired.clipId);
      var nextClipId = patch.clipId == null ? desired.clipId : patch.clipId;
      var nextClip = valid.clips.get(nextClipId);
      var livePosition = currentPosition();
      var nextPosition;
      if (patch.positionSeconds != null) {
        nextPosition = patch.positionSeconds;
      } else if (nextClipId !== desired.clipId) {
        timelinePositions.set(oldClip.timelineId, livePosition);
        nextPosition = oldClip.timelineId === nextClip.timelineId
          ? livePosition
          : (timelinePositions.get(nextClip.timelineId) || 0);
      } else if (patch.playing === false) {
        nextPosition = livePosition;
      } else if (desired.playing && audible.playing) {
        nextPosition = livePosition;
      } else {
        nextPosition = desired.positionSeconds;
      }
      var nextClipKey = safeCanonicalUrl(nextClip.url);
      var knownEntry = nextClipKey == null ? null : decoded.get(nextClipKey);
      var knownBuffer = knownEntry && knownEntry.buffer;
      var knownDuration = knownBuffer && Number(knownBuffer.duration);
      if (!knownDuration && audible.clipId === nextClipId) knownDuration = audible.durationSeconds;
      if (knownDuration > 0) nextPosition %= knownDuration;
      var nextDesired = {
        clipId: nextClipId,
        playing: patch.playing == null ? desired.playing : patch.playing,
        positionSeconds: nextPosition
      };
      intentVersion += 1;
      var version = intentVersion;
      desired = nextDesired;
      if (!desired.playing) {
        var pausedPosition = patch.positionSeconds == null ? currentPosition() : desired.positionSeconds;
        desired = { clipId: desired.clipId, playing: false, positionSeconds: pausedPosition };
        stopSource();
        release(owner);
      }
      if (!desired.playing && audible.clipId != null) {
        audible = {
          clipId: audible.clipId,
          playing: false,
          positionSeconds: desired.positionSeconds,
          durationSeconds: audible.durationSeconds
        };
      }
      if (!desired.playing) {
        publish(audible.clipId == null ? 'idle' : 'paused', null);
        return Promise.resolve(currentState);
      }

      stopSource();
      audible = {
        clipId: audible.clipId,
        playing: false,
        positionSeconds: audible.positionSeconds,
        durationSeconds: audible.durationSeconds
      };
      var clip = valid.clips.get(desired.clipId);
      var clipKey = safeCanonicalUrl(clip.url);
      publish(clipKey != null && decoded.has(clipKey) ? 'ready' : 'loading', null);

      var context;
      var resume;
      try {
        context = getAudibleContext();
        if (platform.configureAudioSession) platform.configureAudioSession();
        try {
          resume = typeof context.resume === 'function' ? context.resume() : Promise.resolve();
        } catch (resumeError) {
          throw publicError(
            'CONTEXT_RESUME_FAILED',
            resumeError && resumeError.message ? resumeError.message : 'Audio context resume failed.',
            clip.id
          );
        }
        resume = Promise.resolve(resume).catch(function (resumeError) {
          throw publicError(
            'CONTEXT_RESUME_FAILED',
            resumeError && resumeError.message ? resumeError.message : 'Audio context resume failed.',
            clip.id
          );
        });
        claim(owner);
      } catch (error) {
        var setupFailure = stableFailure(error, 'UNSUPPORTED_AUDIO', clip.id);
        audible = { clipId: null, playing: false, positionSeconds: 0, durationSeconds: 0 };
        release(owner);
        publish('error', setupFailure);
        return Promise.resolve(currentState);
      }

      return Promise.all([Promise.resolve(resume), loadClip(clip, 'user')])
        .then(function (values) {
          if (destroyed || version !== intentVersion || !desired.playing || desired.clipId !== clip.id) {
            return currentState;
          }
          publish('ready', null);
          try {
            return beginPlayback(version, clip, values[1]);
          } catch (sourceError) {
            stopSource();
            throw publicError(
              'SOURCE_START_FAILED',
              sourceError && sourceError.message ? sourceError.message : 'Audio source start failed.',
              clip.id
            );
          }
        })
        .catch(function (error) {
          if (destroyed || version !== intentVersion) return currentState;
          var failure = stableFailure(error, 'UNSUPPORTED_AUDIO', clip.id);
          audible = {
            clipId: audible.clipId,
            playing: false,
            positionSeconds: audible.positionSeconds,
            durationSeconds: audible.durationSeconds
          };
          release(owner);
          publish('error', failure);
          return currentState;
        });
    }

    function subscribe(listener) {
      assertAlive();
      if (typeof listener !== 'function') {
        throw publicError('INVALID_CONFIG', 'subscribe requires a listener function.');
      }
      listeners.add(listener);
      try {
        listener(currentState);
      } catch (listenerError) {
        report(listenerError, 'listener');
      }
      var disposed = false;
      return function dispose() {
        if (disposed) return;
        disposed = true;
        listeners.delete(listener);
      };
    }

    function destroy() {
      if (destroyed) return;
      var finalPosition = currentPosition();
      destroyed = true;
      intentVersion += 1;
      stopSource();
      safeCall(analyser, 'disconnect');
      release(owner);
      desired = {
        clipId: desired.clipId,
        playing: false,
        positionSeconds: finalPosition
      };
      audible = {
        clipId: audible.clipId,
        playing: false,
        positionSeconds: finalPosition,
        durationSeconds: audible.durationSeconds
      };
      currentState = snapshot(
        desired,
        audible,
        audible.clipId == null ? 'idle' : 'paused',
        currentState.error
      );
      listeners.clear();
      registrations.delete(valid.id);
    }

    var session = Object.freeze({
      get state() { return currentState; },
      set: set,
      prepare: prepare,
      subscribe: subscribe,
      readSpectrum: readSpectrum,
      getWaveform: getWaveform,
      destroy: destroy
    });
    registrations.set(valid.id, owner);
    return session;

    function prepare(clipIds, prepareOptions) {
      assertAlive();
      prepareOptions = prepareOptions || {};
      var priority = prepareOptions.priority;
      if (priority !== 'user' && priority !== 'near' && priority !== 'background') {
        throw publicError('INVALID_CONFIG', 'prepare priority must be user, near, or background.');
      }
      if (!Array.isArray(clipIds)) {
        throw publicError('INVALID_CONFIG', 'prepare requires an array of clip ids.');
      }
      var uniqueIds = Array.from(new Set(clipIds));
      var outcomes = uniqueIds.map(function (clipId) {
        var clip = valid.clips.get(clipId);
        if (!clip) {
          return Promise.resolve({
            ready: false,
            clipId: clipId,
            error: { clipId: clipId, code: 'UNKNOWN_CLIP', message: 'Unknown clip: ' + clipId }
          });
        }
        return loadClip(clip, priority).then(function () {
          return { ready: true, clipId: clipId };
        }, function (error) {
          var failure = stableFailure(error, 'UNSUPPORTED_AUDIO', clipId);
          return {
            ready: false,
            clipId: clipId,
            error: { clipId: clipId, code: failure.code, message: failure.message }
          };
        });
      });
      return Promise.all(outcomes).then(function (results) {
        return {
          ready: results.filter(function (result) { return result.ready; }).map(function (result) { return result.clipId; }),
          failed: results.filter(function (result) { return !result.ready; }).map(function (result) { return result.error; })
        };
      });
    }

    function readSpectrum(into) {
      assertAlive();
      if (!(into instanceof Uint8Array) && !(into instanceof Float32Array)) {
        throw publicError('INVALID_CONFIG', 'readSpectrum requires a Uint8Array or Float32Array.');
      }
      into.fill(0);
      spectrumResult.binsWritten = into.length;
      spectrumResult.sampleRate = analysisSampleRate;
      if (!audible.playing || !analyser || !spectrumScratch) return spectrumResult;

      analyser.getByteFrequencyData(spectrumScratch);
      var count = Math.min(into.length, spectrumScratch.length);
      for (var index = 0; index < count; index += 1) {
        into[index] = into instanceof Uint8Array
          ? spectrumScratch[index]
          : spectrumScratch[index] / 255;
      }
      spectrumResult.binsWritten = count;
      return spectrumResult;
    }

    function getWaveform(clipId, waveformOptions) {
      assertAlive();
      var clip = valid.clips.get(clipId);
      if (!clip) {
        return Promise.reject(Object.freeze({
          clipId: clipId,
          code: 'UNKNOWN_CLIP',
          message: 'Unknown clip: ' + clipId
        }));
      }
      waveformOptions = waveformOptions || {};
      var samples = waveformOptions.samples == null ? 512 : waveformOptions.samples;
      if (!Number.isInteger(samples) || samples <= 0 || samples > 4096) {
        return Promise.reject(Object.freeze({
          clipId: clipId,
          code: 'INVALID_CONFIG',
          message: 'Waveform samples must be an integer from 1 to 4096.'
        }));
      }
      var waveformClipKey = safeCanonicalUrl(clip.url);
      if (waveformClipKey == null) {
        return Promise.reject(stableFailure(invalidUrlError(clip), 'INVALID_URL', clip.id));
      }
      var cacheKey = waveformClipKey + '|' + samples;
      if (waveformCache.has(cacheKey)) return Promise.resolve(waveformCache.get(cacheKey).slice());
      return loadClip(clip, 'near').then(function (buffer) {
        var envelope = extractWaveform(buffer, samples);
        waveformCache.set(cacheKey, envelope);
        return envelope.slice();
      }, function (error) {
        return Promise.reject(stableFailure(error, 'UNSUPPORTED_AUDIO', clipId));
      });
    }

    function extractWaveform(buffer, samples) {
      var envelope = new Float32Array(samples);
      var length = Math.max(0, Number(buffer.length) || 0);
      var channels = Math.max(1, Number(buffer.numberOfChannels) || 1);
      for (var sampleIndex = 0; sampleIndex < samples; sampleIndex += 1) {
        var start = Math.floor(sampleIndex * length / samples);
        var end = Math.max(start + 1, Math.floor((sampleIndex + 1) * length / samples));
        var peak = 0;
        for (var channel = 0; channel < channels; channel += 1) {
          var data = buffer.getChannelData(channel);
          var limit = Math.min(end, data.length);
          for (var frame = start; frame < limit; frame += 1) {
            peak = Math.max(peak, Math.abs(data[frame]));
          }
        }
        envelope[sampleIndex] = Math.min(1, peak);
      }
      return envelope;
    }
  }

  function registerParticipant(config) {
    if (!config || typeof config.id !== 'string' || !config.id.trim() || typeof config.pause !== 'function') {
      throw publicError('INVALID_CONFIG', 'An audible participant requires an id and pause callback.');
    }
    if (registrations.has(config.id)) {
      throw publicError('INVALID_CONFIG', 'The media id is already registered: ' + config.id);
    }
    var destroyed = false;
    var participant = { id: config.id, pause: config.pause };
    registrations.set(config.id, participant);
    return Object.freeze({
      claim: function () {
        if (!destroyed) claim(participant);
      },
      release: function () {
        if (!destroyed) release(participant);
      },
      destroy: function () {
        if (destroyed) return;
        destroyed = true;
        release(participant);
        registrations.delete(config.id);
      }
    });
  }

  return Object.freeze({
    createSession: createSession,
    registerParticipant: registerParticipant
  });

  function createBrowserPlatform() {
    return {
      canonicalizeUrl: function (url) {
        return new browserRoot.URL(
          url,
          browserRoot.location && browserRoot.location.href ? browserRoot.location.href : undefined
        ).href;
      },
      fetchArrayBuffer: function (url) {
        return browserRoot.fetch(url).then(function (response) {
          if (!response.ok) throw new Error('Audio request failed with status ' + response.status + '.');
          return response.arrayBuffer();
        });
      },
      decodeAudioData: function (bytes, context) {
        if (context && typeof context.decodeAudioData === 'function') return context.decodeAudioData(bytes);
        var OfflineContext = browserRoot.OfflineAudioContext || browserRoot.webkitOfflineAudioContext;
        if (!OfflineContext) throw publicError('UNSUPPORTED_AUDIO', 'Audio decoding is unavailable.');
        return new OfflineContext(1, 1, 44100).decodeAudioData(bytes);
      },
      createAudibleContext: function () {
        var Context = browserRoot.AudioContext || browserRoot.webkitAudioContext;
        if (!Context) throw publicError('UNSUPPORTED_AUDIO', 'Web Audio is unavailable.');
        return new Context();
      },
      configureAudioSession: function () {
        if (browserRoot.navigator && browserRoot.navigator.audioSession) {
          browserRoot.navigator.audioSession.type = 'playback';
        }
      },
      requestAnimationFrame: function (callback) { return browserRoot.requestAnimationFrame(callback); },
      cancelAnimationFrame: function (id) { browserRoot.cancelAnimationFrame(id); },
      log: function (entry) {
        if (browserRoot.console && browserRoot.console.error) browserRoot.console.error(entry);
      }
    };
  }
}));
